import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import axios from 'axios';
import './NavbarSearch.css';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const ContractIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#1890ff" strokeWidth="2" width="14" height="14">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

const ShipmentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#52c41a" strokeWidth="2" width="14" height="14">
    <path d="M5 18H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H21a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-2"/>
    <circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>
  </svg>
);

const ContainerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#fa8c16" strokeWidth="2" width="14" height="14">
    <rect x="1" y="3" width="22" height="18" rx="2" ry="2"/><line x1="1" y1="9" x2="23" y2="9"/>
  </svg>
);

interface SearchResult {
  type: 'contract' | 'shipment' | 'container';
  reference: string;
  sellerRef?: string;
  buyerRef?: string;
  bookingNumber?: string;
  containerNumber?: string;
  supplier?: string;
  buyer?: string;
  status?: string;
}

interface SearchResponse {
  results: SearchResult[];
  redirect: string | null;
  searchedFields?: string[];
}

const NavbarSearch: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcut (Alt+S) to focus search
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 's') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchValue.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get<SearchResponse>(
          `/api/v1/search?q=${encodeURIComponent(searchValue.trim())}`
        );
        setResults(response.data.results);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleDirectSearch = async () => {
    const value = searchValue.trim();
    if (!value) return;

    setLoading(true);
    setShowDropdown(false);
    
    try {
      const response = await axios.get<SearchResponse>(
        `/api/v1/search?q=${encodeURIComponent(value)}`
      );

      const { results: searchResults, redirect } = response.data;

      if (redirect) {
        navigate(redirect);
        message.success(`Found contract: ${searchResults[0]?.reference}`);
      } else if (searchResults.length > 0) {
        message.info(`Found ${searchResults.length} matches. Showing first result.`);
        navigate(`/contracts/${searchResults[0]?.reference}`);
      } else {
        message.warning('No matching contracts or shipments found');
      }
    } catch (error) {
      console.error('Search error:', error);
      message.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
      setSearchValue('');
    }
  };

  const handleSelect = (result: SearchResult) => {
    navigate(`/contracts/${result.reference}`);
    setSearchValue('');
    setShowDropdown(false);
    setResults([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else {
        handleDirectSearch();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  };

  const getMatchedField = (result: SearchResult): { label: string; value: string } | null => {
    const q = searchValue.toLowerCase();
    if (result.sellerRef?.toLowerCase().includes(q)) return { label: 'Seller Ref', value: result.sellerRef };
    if (result.buyerRef?.toLowerCase().includes(q)) return { label: 'Buyer Ref', value: result.buyerRef };
    if (result.bookingNumber?.toLowerCase().includes(q)) return { label: 'Booking', value: result.bookingNumber };
    if (result.containerNumber?.toLowerCase().includes(q)) return { label: 'Container', value: result.containerNumber };
    if (result.supplier?.toLowerCase().includes(q)) return { label: 'Supplier', value: result.supplier };
    if (result.buyer?.toLowerCase().includes(q)) return { label: 'Buyer', value: result.buyer };
    return null;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'contract': return <ContractIcon />;
      case 'shipment': return <ShipmentIcon />;
      case 'container': return <ContainerIcon />;
      default: return <ContractIcon />;
    }
  };

  return (
    <div className="navbar-search-container" ref={containerRef}>
      <div className={`navbar-search ${isFocused ? 'focused' : ''} ${loading ? 'loading' : ''}`}>
        <SearchIcon />
        <input
          ref={inputRef}
          type="search"
          placeholder="Search contracts..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-label="Search contracts"
        />
        {loading && <span className="search-spinner" />}
      </div>
      
      {showDropdown && (
        <div className="navbar-search-dropdown">
          {results.length === 0 ? (
            <div className="navbar-search-empty">No results found</div>
          ) : (
            results.map((result, index) => {
              const matched = getMatchedField(result);
              return (
                <div
                  key={`${result.reference}-${index}`}
                  className={`navbar-search-result ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="result-header">
                    <span className="result-reference">
                      {getTypeIcon(result.type)}
                      {result.reference}
                    </span>
                    {result.status && <span className="result-status">{result.status}</span>}
                  </div>
                  {matched && (
                    <div className="result-matched">
                      <strong>{matched.label}:</strong> {matched.value}
                    </div>
                  )}
                  {(result.supplier || result.buyer) && (
                    <div className="result-parties">
                      {result.supplier} {result.supplier && result.buyer && '→'} {result.buyer}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NavbarSearch;
